import { Link } from "@tanstack/react-router";
import { AlertCircle, Plus } from "lucide-react";
import type { ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import type { SectionIcon } from "@/lib/owner-sections";
import { cn } from "@/lib/utils";

/**
 * Default loading placeholder for a tab- or page-sized content region.
 *
 * Renders shimmer rows that mirror the final layout shape rather than a
 * spinner + "Loading…" string. Per DESIGN_GUIDELINES §5, skeletons are the
 * preferred loading UX for any content panel; bare loader text is reserved
 * for tiny inline regions and sub-1s auth handoffs.
 *
 * Pick a variant that roughly matches what's about to render:
 *  - `rows` (default) — for table-style lists. Renders a header bar + N
 *    full-width rows inside a bordered surface.
 *  - `cards` — for grouped sections (oauth-apps, settings). Each "card" is
 *    a bordered block with a title bar + a few rows.
 *  - `panel` — for a single content panel with a heading; one title bar +
 *    a few paragraph-width bars.
 *  - `list-picker` — search field + stacked bordered pick rows (clone dialogs).
 *  - `table` — bordered table with header cells + data rows (compact tables).
 *
 * The `label` is exposed via `aria-label` so screen readers still hear
 * "Loading <thing>…" without users seeing it.
 */
export function TabLoadingState({
	label,
	variant = "rows",
	rows = 5,
	cards = 2,
	className,
}: {
	label: string;
	variant?: "rows" | "cards" | "panel" | "list-picker" | "table";
	rows?: number;
	cards?: number;
	className?: string;
}) {
	if (variant === "list-picker") {
		return (
			<div
				role="status"
				aria-label={`Loading ${label}`}
				className={cn("space-y-3", className)}
			>
				<Skeleton className="h-9 w-full rounded-md" />
				<div className="space-y-2">
					{Array.from({ length: rows }).map((_, i) => (
						<div
							// biome-ignore lint/suspicious/noArrayIndexKey: static skeleton rows
							key={i}
							className="space-y-2 rounded-md border p-3"
						>
							<Skeleton className="h-4 w-2/5" />
							<Skeleton className="h-3 w-1/2" />
						</div>
					))}
				</div>
			</div>
		);
	}

	if (variant === "table") {
		return (
			<div
				role="status"
				aria-label={`Loading ${label}`}
				className={cn("overflow-hidden rounded-lg border", className)}
			>
				<div className="flex gap-4 border-b px-4 py-3">
					<Skeleton className="h-3 w-16" />
					<Skeleton className="h-3 w-12" />
					<Skeleton className="h-3 flex-1" />
				</div>
				<div className="divide-y">
					{Array.from({ length: rows }).map((_, i) => (
						<div
							// biome-ignore lint/suspicious/noArrayIndexKey: static skeleton rows
							key={i}
							className="flex items-center gap-4 px-4 py-3"
						>
							<Skeleton className="h-3 w-20 shrink-0" />
							<Skeleton className="h-5 w-16 shrink-0 rounded-full" />
							<Skeleton className="h-3 flex-1" />
						</div>
					))}
				</div>
			</div>
		);
	}

	if (variant === "cards") {
		return (
			<div
				role="status"
				aria-label={`Loading ${label}`}
				className={cn("space-y-4", className)}
			>
				{Array.from({ length: cards }).map((_, i) => (
					<div
						// biome-ignore lint/suspicious/noArrayIndexKey: static skeleton blocks
						key={i}
						className="space-y-3 rounded-lg border p-4"
					>
						<Skeleton className="h-4 w-1/3" />
						<div className="space-y-2">
							<Skeleton className="h-3 w-full" />
							<Skeleton className="h-3 w-5/6" />
							<Skeleton className="h-3 w-3/4" />
						</div>
					</div>
				))}
			</div>
		);
	}

	if (variant === "panel") {
		return (
			<div
				role="status"
				aria-label={`Loading ${label}`}
				className={cn("space-y-3", className)}
			>
				<Skeleton className="h-5 w-1/4" />
				<Skeleton className="h-3 w-full" />
				<Skeleton className="h-3 w-5/6" />
				<Skeleton className="h-3 w-2/3" />
			</div>
		);
	}

	return (
		<div
			role="status"
			aria-label={`Loading ${label}`}
			className={cn("rounded-lg border", className)}
		>
			<div className="border-b px-4 py-3">
				<Skeleton className="h-3 w-1/4" />
			</div>
			<div className="divide-y">
				{Array.from({ length: rows }).map((_, i) => (
					<div
						// biome-ignore lint/suspicious/noArrayIndexKey: static skeleton rows
						key={i}
						className="flex items-center gap-3 px-4 py-3"
					>
						<Skeleton
							className="h-3 flex-1"
							style={{ maxWidth: `${50 + ((i * 17) % 30)}%` }}
						/>
						<Skeleton className="ml-auto h-3 w-16 shrink-0" />
					</div>
				))}
			</div>
		</div>
	);
}

/** Router default pending shell — panel-shaped, not the table `rows` variant. */
export function PageRoutePendingShell() {
	return (
		<div className="px-4 pt-2.5 pb-8 lg:px-6">
			<TabLoadingState label="page" variant="panel" />
		</div>
	);
}

export function TabErrorState({ label }: { label: string }) {
	return (
		<div className="flex items-center justify-center py-12 text-destructive">
			<AlertCircle className="mr-2 h-4 w-4" />
			Failed to load {label}
		</div>
	);
}

export function TabEmptyState({
	icon: Icon,
	title,
	description,
	actionLabel,
	onAction,
	// Optional secondary action — rendered as a quieter button beside the
	// primary one. `secondaryActionTo` makes it a router link; otherwise pass
	// `secondaryOnAction`. Used e.g. on the agents empty state ("Create agent"
	// + "Link your agent").
	secondaryActionLabel,
	secondaryActionTo,
	secondaryOnAction,
	iconElement,
	children,
}: {
	icon: SectionIcon;
	title: string;
	description: string;
	actionLabel?: string;
	onAction?: () => void;
	secondaryActionLabel?: string;
	secondaryActionTo?: string;
	secondaryOnAction?: () => void;
	iconElement?: ReactNode;
	children?: ReactNode;
}) {
	const hasPrimary = Boolean(actionLabel && onAction);
	const hasSecondary = Boolean(
		secondaryActionLabel && (secondaryActionTo || secondaryOnAction),
	);

	return (
		<div className="flex flex-col items-center justify-center py-12 text-center text-muted-foreground">
			{iconElement ?? <Icon className="h-12 w-12 mb-4 opacity-50" />}
			<p className="text-lg font-medium">{title}</p>
			<p className="text-sm mt-1">{description}</p>
			{children}
			{(hasPrimary || hasSecondary) && (
				<div className="mt-4 flex flex-wrap items-center justify-center gap-2">
					{hasPrimary && (
						<Button variant="default" onClick={onAction}>
							<Plus className="mr-2 h-4 w-4" />
							{actionLabel}
						</Button>
					)}
					{hasSecondary &&
						(secondaryActionTo ? (
							<Button asChild variant="outline">
								<Link to={secondaryActionTo as "/"}>
									{secondaryActionLabel}
								</Link>
							</Button>
						) : (
							<Button variant="outline" onClick={secondaryOnAction}>
								{secondaryActionLabel}
							</Button>
						))}
				</div>
			)}
		</div>
	);
}
